import React from 'react';
import { Route, NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import Select from 'react-select'; /* for date selector */
import moment from 'moment';
import ResizeThrottler from 'APP_ROOT/components/ResizeThrottler';
import globals from 'APP_ROOT/components/common/globals';
import { strings, getCurrentLocale } from 'APP_ROOT/localization';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize'; // "to continue using CSS stylesheets and classes" https://github.com/airbnb/react-dates
import TagsTreeviewDropdown from '../../components/TagsTreeViewDropDown';
import Protected from '../../components/common/protected';

const RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS = 100; // IMPORTANT: must be a greater value than RESIZE_EVENT_DISPATCH_INTERVAL_MS (togglePanels(...) triggers resize event directly)

const MAX_WIDTH_FOR_DATE_SELECTOR = 576;

const DATE_PICKER_OPTIONS = [
    { value: 'today', label: strings.date_picker_options_today_label },
    { value: '24hour', label: strings.date_picker_options_24hour_label },
    { value: '48hour', label: strings.date_picker_options_48hour_label },
    { value: 'week', label: strings.date_picker_options_week_label },
    { value: '2weeks', label: strings.date_picker_options_2weeks_label },
    { value: 'month', label: strings.date_picker_options_month_label },
    { value: 'custom', label: strings.date_picker_options_custom_label }
];

const DATE_PICKER_OPTIONS_INDEX_FOR_CUSTOM = 6;

const GROUP_OPTIONS = [
    {
        label: strings.reports_groups_options_vehicle_label,
        value: 'vehicle'
    },
    {
        label: strings.reports_groups_options_driver_label,
        badge: strings[globals.facialRecognitionBadgeStringKey].toLocaleUpperCase(),
        value: 'driver'
    },
    {
        label: strings.reports_groups_options_vehicle_then_driver_label,
        badge: strings[globals.facialRecognitionBadgeStringKey].toLocaleUpperCase(),
        value: 'vehicle,driver'
    },
    {
        label: strings.reports_groups_options_driver_then_vehicle_label,
        badge: strings[globals.facialRecognitionBadgeStringKey].toLocaleUpperCase(),
        value: 'driver,vehicle'
    },
];

const SHOW_DRIVING_PARKED_OPTIONS = [
    {
        label: strings.report_show_driving_parked_trip_label,
        value: 'trip'
    },
    {
        label: strings.report_show_driving_parked_parked_label,
        value: 'parked'
    },
    {
        label: strings.report_show_driving_parked_both_label,
        value: 'both'
    },
];

const SHOW_PARKED_IDLE_OPTIONS = [
    {
        label: strings.report_show_driving_parked_parked_label,
        value: 'parked'
    },
    {
        label: strings.report_show_driving_parked_idle_label,
        value: 'idling'
    },
    {
        label: strings.report_show_parked_idle_both_label,
        value: 'both'
    },
];

const renderGroupByOption = option => (
    <div className='d-flex align-items-center justify-content-between'>
        <span>{option.label}</span>
        {option.badge && <span className='badge badge-secondary ml-1'>{option.badge}</span>}
    </div>
)

export default class ReportsHeader extends React.PureComponent {

    constructor (props) {

        super(props);

        this.state = {
            selectedDateIndex: 2,
            // FIXME - startDate default is also defined in Reports.js
            startDate: moment().subtract(2, 'days'),
            endDate: moment(),
            windowWidth: window.innerWidth,
        }
    }

    handleTagsChange = (selectedTagIds) => {
        this.props.handleFilterByTagsChange(selectedTagIds);
    }

    resizeCallback = () => {
        this.setState({windowWidth: window.innerWidth});
    }

    onSelectChange = (selectedDateOption) => {

        var selectedDateIndex;

        DATE_PICKER_OPTIONS.forEach((dateOption, dateOptionIndex) => {
            if (dateOption.value === selectedDateOption.value) {
                selectedDateIndex = dateOptionIndex;
            }
        });

        if (isNaN(selectedDateIndex)) {
            console.error("Reports date picker configuration error. Unknown selectedDateOption, " + JSON.stringify(selectedDateOption) + ", not found in DATE_PICKER_OPTIONS", DATE_PICKER_OPTIONS);
            return;
        }

        var selectDateOption = DATE_PICKER_OPTIONS[selectedDateIndex];

        var datestamp;

        if (selectDateOption.value === 'today') {
            datestamp = moment().startOf('day');
        } else if (selectDateOption.value === 'yesterday') {
            datestamp = moment().startOf('day').subtract(1, 'days');
        } else if (selectDateOption.value === '24hour') {
            datestamp = moment().subtract(1, 'days');
        } else if (selectDateOption.value === '48hour') {
            datestamp = moment().subtract(2, 'days');
        } else if (selectDateOption.value === 'week') {
            datestamp = moment().subtract(7, 'days');
        } else if (selectDateOption.value === '2weeks') {
            datestamp = moment().subtract(14, 'days');
        } else if (selectDateOption.value === 'month') {
            datestamp = moment().subtract(31, 'days');
        } else if (selectDateOption.value === 'year') {
            datestamp = moment().subtract(365, 'days');
        } else if (selectDateOption.value === 'custom') {
            this.setState({
                selectedDateIndex: selectedDateIndex
            });
            return;
        } else {
            datestamp = moment().subtract(3650, 'days');
        }

        this.setState({
            selectedDateIndex: selectedDateIndex,
            startDate: datestamp,
            endDate: moment()
        },
            this.notifyParent
        );


    }

    onCustomDateSelect = (dates) => {
        this.setState({
            startDate: dates.startDate,
            endDate: dates.endDate,
            chartError: false,
            selectedDateIndex: DATE_PICKER_OPTIONS_INDEX_FOR_CUSTOM
        },
            this.notifyParent
        );
    }

    notifyParent = () => {
        if(this.state.startDate)
        {
            this.props.selectDateRange([ this.state.startDate.toDate(), this.state.endDate ? this.state.endDate.toDate(): this.state.startDate.toDate() ]);
        }
    }

    checkDateIsOutside = (day) => {
        return false;
    }

    handleGroupChange = group => {
        const value = group && group.value;
        this.props.handleGroupByChange(value);
    }

    handleShowDrivingParkingChange = showDrivingParking => {
        const value = showDrivingParking && showDrivingParking.value;
        this.props.handleShowDrivingParkingChange(value);
    }

    render() {
        const selectedDate = DATE_PICKER_OPTIONS[this.state.selectedDateIndex];
        const currentLocale = getCurrentLocale();

        let reportsTitle = strings.reports_raven_reports_title;
        switch (this.props.match.params.parentGroupingType) {
            case "trip":
                reportsTitle = strings.reports_trip_report_filter_name;
                break;
            case "event":
                reportsTitle = strings.reports_event_report_filter_name;
                break;
            case "geofence":
                reportsTitle = strings.reports_geofence_report_filter_name;
                break;
            case "raven":
                reportsTitle = strings.reports_raven_report_filter_name;
                break;
            case "asset":
                reportsTitle = strings.reports_asset_report_filter_name;
                break;
            case "dtc":
                reportsTitle = strings.reports_dtc_report_filter_name;
                break;
            case "stops":
                reportsTitle = strings.reports_stops_report_filter_name;
                break;
            case "activity":
                reportsTitle = strings.reports_activity_report_filter_name;
                break;
            default:
                break;
        }

        return (
            <div className="row">
                <div className="col-12">
                    <h1 className="d-inline-block">{reportsTitle}</h1>
                </div>
                <div className="col-12 fleet-report-filters">
                    <div className="report-filter-type">
                        <NavLink to={'/' + currentLocale + '/stats/reports/activity'} className={"btn driver-vehicle"}>{strings.reports_activity_report_filter_name}</NavLink>
                        <NavLink to={'/' + currentLocale + '/stats/reports/trip'} className={"btn driver-vehicle"}>{strings.reports_trip_report_filter_name}</NavLink>
                        <NavLink to={'/' + currentLocale + '/stats/reports/event'} className={"btn driver-vehicle"}>{strings.reports_event_report_filter_name}</NavLink>
                        <Protected resource="geofences" action="read"><NavLink to={'/' + currentLocale + '/stats/reports/geofence'} className={"btn driver-vehicle"}>{strings.reports_geofence_report_filter_name}</NavLink></Protected>
                        <NavLink to={'/' + currentLocale + '/stats/reports/raven'} className={"btn driver-vehicle"}>{strings.reports_raven_report_filter_name}</NavLink>
                        <NavLink to={'/' + currentLocale + '/stats/reports/dtc'} className={"btn driver-vehicle"}>{strings.reports_dtc_report_filter_name}</NavLink>
                        <NavLink to={'/' + currentLocale + '/stats/reports/stops'} className={"btn driver-vehicle"}>{strings.reports_stops_report_filter_name}</NavLink>
                        <div className="report-group-and-type-options">

                        <Route
                            path='/:lang/stats/reports/:report'
                            render={({match}) => {
                                if(!this.props.tags) return null;
                                if(this.props.tagsError) return null;
                                if(this.props.tagsLoading) return null;
                                if(this.props.tags.length === 0) return null;
                                if (match.params.report === 'asset') return null;

                                return (
                                    <div className="filter-by-tags">
                                        <span>{strings.reports_raven_filter_by}</span>
                                        <TagsTreeviewDropdown
                                            tags={this.props.tags}
                                            onChange={this.handleTagsChange}
                                            multipleSelection={true}
                                            selectedTagsIds={this.props.filterByTags}
                                            placeholder={strings.tags_list_select_tags_placeholder}
                                            className="report-filter-by-tags-select"
                                            useReactSelectComponent
                                            compact
                                        />
                                    </div>
                                );
                            }}
                            />

                            { this.props.features.facial_recognition ? 
                                <Route
                                    path='/:lang/stats/reports/:report'
                                    render={({match}) => {
                                        if (match.params.report === 'raven') return null;
                                        if (match.params.report === 'asset') return null;
                                        if (match.params.report === 'dtc') return null;
                                        return (
                                            <div className="sort-by">
                                                <span>{strings.reports_raven_sort_by}</span>
                                                <Select id="report-group-select"
                                                    options={GROUP_OPTIONS}
                                                    value={this.props.groupBy}
                                                    name="report-group"
                                                    onChange={this.handleGroupChange}
                                                    clearable={false}
                                                    searchable={false}
                                                    optionRenderer={renderGroupByOption}
                                                />
                                            </div>
                                        );
                                    }}
                                />
                            : null }
                            <Route
                                path='/:lang/stats/reports/:report'
                                render={({match}) => {
                                    if (match.params.report === 'geofence') return null;
                                    if (match.params.report === 'raven') return null;
                                    if (match.params.report === 'asset') return null;
                                    if (match.params.report === 'dtc') return null;
                                    if (match.params.report === 'activity') return null;
                                    if (match.params.report === 'stops' ) {
                                        return (
                                            <div className="show-type">
                                                <span>{strings.reports_raven_show}</span>
                                                <Select
                                                    id="report-show-driving-parking-select"
                                                    options={SHOW_PARKED_IDLE_OPTIONS}
                                                    value={this.props.showDrivingParking}
                                                    name="report-show-driving-parking"
                                                    onChange={this.handleShowDrivingParkingChange}
                                                    clearable={false}
                                                    searchable={false}
                                                />
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="show-type">
                                            <span>{strings.reports_raven_show}</span>
                                            <Select
                                                id="report-show-driving-parking-select"
                                                options={SHOW_DRIVING_PARKED_OPTIONS}
                                                value={this.props.showDrivingParking}
                                                name="report-show-driving-parking"
                                                onChange={this.handleShowDrivingParkingChange}
                                                clearable={false}
                                                searchable={false}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <Route
                        path='/:lang/stats/reports/:report'
                        render={({match}) => {
                            if (match.params.report === 'raven') return null;
                            if (match.params.report === 'asset') return null;
                            return (
                                <div className="report-filter-date">
                                    <div className="report-date-select-wrapper">
                                        <div className="report-date-select-icon"></div>
                                        <ResizeThrottler interval={RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS} callback={this.resizeCallback} />
                                        <Select id="report-date-select"
                                            options={DATE_PICKER_OPTIONS}
                                            value={selectedDate}
                                            name="report-date"
                                            onChange={this.onSelectChange}
                                            clearable={false}
                                            searchable={false} />
                                        <DateRangePicker
                                            disabled = { false }
                                            startDate={this.state.startDate}
                                            startDateId="your_unique_start_date_id"
                                            endDate={this.state.endDate}
                                            endDateId="your_unique_end_date_id"
                                            onDatesChange={this.onCustomDateSelect}
                                            focusedInput={this.state.dateFocusedInput}
                                            onFocusChange={focusedInput => this.setState({ dateFocusedInput: focusedInput })}
                                            enableOutsideDays={true}
                                            minimumNights={0}
                                            orientation={this.state.windowWidth > MAX_WIDTH_FOR_DATE_SELECTOR ? "horizontal" : "vertical"}
                                            withFullScreenPortal={this.state.windowWidth > MAX_WIDTH_FOR_DATE_SELECTOR ? false : true}
                                            withPortal={this.state.windowWidth > MAX_WIDTH_FOR_DATE_SELECTOR ? false : true}
                                            isDayBlocked={() => { return false } }
                                            isOutsideRange={this.checkDateIsOutside}
                                            /* the foloowing props are prep for easier CSS styling */
                                            noBorder={true} /* do not wrap a border around this component */
                                            small={true} /* use more compact padding, input size, and arrow */
                                            block={true} /* scale-to-fit parent div width */
                                        />

                                    </div>
                                </div>
                            );
                        }} 
                    />
                </div>
            </div>
        )
    }
}

ReportsHeader.propTypes = {
    selectDateRange: PropTypes.func.isRequired,
    handleGroupByChange: PropTypes.func.isRequired,
    handleShowDrivingParkingChange: PropTypes.func.isRequired,
}
